import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';
import {
    PersonaType,
    FindPeopleRequest,
    FindPeopleJsonResponse,
    IndexedPageView,
    PersonaResponseShape,
    getServiceAsync,
    TFactories,
} from '../../../service';
import {
    runOwsRequestWithMonitor,
    Feature,
    fullResponseOperationOption,
    uniqBy,
    getFindPeopleContext,
} from '../../../shared';
import {
    getFindPeopleRequest,
    QueryData,
    createFederatedSearchQueryData,
} from '../../../searchbox';

export const defaultPageView: IndexedPageView = {
    BasePoint: 'Beginning',
    Offset: 0,
    MaxEntriesReturned: 50,
};

export type IndexedPageViewExtraOptions = {
    Offset?: number;
    MaxEntriesReturned?: number;
};

const getDefaultShape = (serviceFactory: TFactories) => {
    const defaultShape: PersonaResponseShape = {
        BaseShape: 'Default',
        AdditionalProperties: [
            serviceFactory.propertyUri({ FieldURI: 'PersonaAlias' }),
            serviceFactory.propertyUri({ FieldURI: 'PersonaRelevanceScore' }),
            serviceFactory.propertyUri({ FieldURI: 'PersonaTitle' }),
        ],
    };
    return defaultShape;
};

function buildRequest(
    serviceFactory: TFactories,
    queryString: string,
    searchRecipientCache: boolean,
    overridePageViewOptions?: IndexedPageViewExtraOptions
): FindPeopleRequest {
    return {
        IndexedPageItemView: serviceFactory.indexedPageView({
            ...defaultPageView,
            ...overridePageViewOptions,
        }),
        PersonaShape: serviceFactory.personaResponseShape(getDefaultShape(serviceFactory)),
        QueryString: queryString,
        QuerySources: ['Mailbox'],
        SearchPeopleSuggestionIndex: searchRecipientCache,
        ShouldResolveOneOffEmailAddress: false,
        Context: getFindPeopleContext(),
    };
}

function createRequest(
    queryString: string,
    searchRecipientCache: boolean,
    monitorName: string,
    overridePageViewOptions?: IndexedPageViewExtraOptions
): Promise<PersonaType[]> {
    return getServiceAsync().then(service =>
        runOwsRequestWithMonitor({
            scenarioName: monitorName,
            feature: Feature.PickerSearch,
            operation: () =>
                service.Operation.findPeopleOperation(
                    service.Factory.findPeopleJsonRequest({
                        Header: getJsonRequestHeader(),
                        Body: service.Factory.findPeopleRequest(
                            buildRequest(
                                service.Factory,
                                queryString,
                                searchRecipientCache,
                                overridePageViewOptions
                            )
                        ),
                    }),
                    fullResponseOperationOption
                ),
            transformResponse: (response: FindPeopleJsonResponse) => response.Body.ResultSet,
        })
    );
}

export function getFilteredPeopleTypeFromSuggestions(
    queryString: string,
    defaultPageExtraViewOptions?: IndexedPageViewExtraOptions
): Promise<PersonaType[]> {
    return createRequest(
        queryString,
        true,
        'GetFilteredPeopleTypeFromSuggestions',
        defaultPageExtraViewOptions
    );
}

function searchFederatedSources(
    queryData: QueryData,
    maxResults: number,
    monitorName: string
): Promise<PersonaType[]> {
    return getServiceAsync().then(service =>
        runOwsRequestWithMonitor({
            scenarioName: monitorName,
            feature: Feature.PickerSearch,
            operation: () =>
                service.Operation.findPeopleOperation(
                    service.Factory.findPeopleJsonRequest({
                        Header: getJsonRequestHeader(),
                        Body: service.Factory.findPeopleRequest(
                            getFindPeopleRequest(service.Factory, queryData, true, maxResults)
                        ),
                    }),
                    fullResponseOperationOption
                ),
            transformResponse: (owsResponse: FindPeopleJsonResponse) =>
                uniqBy(owsResponse.Body.ResultSet, persona => persona.PersonaId.Id),
        })
    );
}

export function getFilteredPeopleTypeFromContacts(
    queryString: string,
    overridePageViewOptions?: IndexedPageViewExtraOptions
): Promise<PersonaType[]> {
    return createRequest(
        queryString,
        false,
        'GetFilteredPeopleTypeFromContacts',
        overridePageViewOptions
    );
}

export function getFilteredPeopleTypeFromFederatedSources(
    queryString: string,
    maxResults: number
): Promise<PersonaType[]> {
    const queryData = createFederatedSearchQueryData(queryString, '');
    return searchFederatedSources(
        queryData,
        maxResults,
        'GetFilteredPeopleTypeFromFederatedSources'
    );
}

export function getSuggestedPeopleType(
    options: IndexedPageViewExtraOptions
): Promise<PersonaType[]> {
    return getFilteredPeopleTypeFromSuggestions(null, options);
}
